import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { CopyPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MESES_ABREV, numeroBR } from "@shared/irrigacao";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { useGradeDeMetas, useSalvarMetas } from "@/hooks/use-irrigacao";

// A grade setor × mês.
//
// A meta é por MÊS DO ANO, não por data: é sazonalidade. A de agosto vale para todo
// agosto, todo ano, até alguém trocar — e a aderência de um período compara cada dia com
// a meta do mês daquele dia.
//
// Célula vazia = SEM META, e sem meta o setor aparece com o valor cru e sem aderência.
// Nunca 0% — o sistema não inventa alvo.
//
// A unidade é a da cultura do setor (banana em mm/dia, citros em L/planta/dia) e a tela
// NUNCA converte: em gotejo, mm sobre a área total não significa nada.

const MES_ATUAL = Number(hojeNaFazenda().slice(5, 7));

function chave(setorId: string, mes: number): string {
  return `${setorId}:${mes}`;
}

export function GradeDeMetas() {
  const { toast } = useToast();
  const grade = useGradeDeMetas();
  const salvar = useSalvarMetas();
  // Só as células EDITADAS ficam aqui — o resto vem do servidor. Guardar a grade inteira
  // em estado local faria um salvamento reescrever célula que ninguém tocou.
  const [rascunho, setRascunho] = useState<Record<string, string>>({});

  const salvas = useMemo(() => {
    const mapa = new Map<string, number>();
    const lista = grade.data?.metas ?? [];
    for (let i = 0; i < lista.length; i++) mapa.set(chave(lista[i].setorId, lista[i].mes), lista[i].valor);
    return mapa;
  }, [grade.data]);

  const valorDe = (setorId: string, mes: number): string => {
    const k = chave(setorId, mes);
    if (k in rascunho) return rascunho[k];
    const salva = salvas.get(k);
    return salva === undefined ? "" : numeroBR(salva, 1);
  };

  const editar = (setorId: string, mes: number, valor: string) => {
    setRascunho(atual => ({ ...atual, [chave(setorId, mes)]: valor }));
  };

  /** Repete o valor do mês nos meses seguintes — 13 setores × 12 meses são 156 células. */
  const repetirNoAno = (setorId: string, mes: number) => {
    const valor = valorDe(setorId, mes);
    setRascunho(atual => {
      const novo = { ...atual };
      for (let m = mes; m <= 12; m++) novo[chave(setorId, m)] = valor;
      return novo;
    });
    toast({ title: `Valor repetido até dezembro`, description: "Confira e clique em Salvar." });
  };

  const pendentes = Object.keys(rascunho);

  const gravar = async () => {
    const metas = pendentes.map(k => {
      const [setorId, mes] = k.split(":");
      const bruto = rascunho[k].trim();
      // Campo vazio APAGA a meta do mês. Diferente de 0, que seria o alvo explícito de
      // "não regar" — e por isso continua sendo um valor gravável.
      return { setorId, mes: Number(mes), valor: bruto === "" ? null : bruto };
    });
    try {
      const r = await salvar.mutateAsync(metas);
      setRascunho({});
      toast({ title: `${r.gravadas} metas salvas` });
    } catch (e: any) {
      toast({ title: "Não foi possível salvar", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <h2 className="font-semibold">Metas de lâmina — setor × mês</h2>
          <p className="text-sm text-muted-foreground">
            A meta do dia é a do mês corrente · a unidade vem da cultura do setor.
          </p>
        </div>
        {pendentes.length > 0 && (
          <Button size="sm" onClick={gravar} disabled={salvar.isPending}>
            {salvar.isPending ? "Salvando…" : `Salvar ${pendentes.length} ${pendentes.length === 1 ? "célula" : "células"}`}
          </Button>
        )}
      </div>

      {grade.isLoading ? (
        <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
      ) : grade.isError ? (
        <p className="p-6 text-center text-sm text-erro">Erro ao carregar as metas.</p>
      ) : (grade.data?.setores.length ?? 0) === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          Nenhum setor cadastrado ainda — as linhas desta grade vêm de Admin · Setores.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="sticky left-0 z-10 bg-background px-3 py-2 text-left font-medium">Setor</th>
                {MESES_ABREV.map((m, i) => (
                  <th
                    key={m}
                    className={`px-1 py-2 text-center font-medium ${i + 1 === MES_ATUAL ? "bg-info-suave text-info" : ""}`}
                  >
                    {m}
                  </th>
                ))}
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {grade.data!.setores.map(setor => (
                <tr key={setor.id} className="border-b last:border-0">
                  <td className="sticky left-0 z-10 bg-background px-3 py-1.5 whitespace-nowrap">
                    <span className="font-medium">{setor.nome}</span>
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {setor.culturaNome ? `${setor.culturaNome} · ` : ""}{setor.unidadeRotulo}/dia
                    </span>
                  </td>
                  {MESES_ABREV.map((_, i) => {
                    const mes = i + 1;
                    const k = chave(setor.id, mes);
                    return (
                      <td key={mes} className={`px-0.5 py-1 ${mes === MES_ATUAL ? "bg-info-suave" : ""}`}>
                        <Input
                          value={valorDe(setor.id, mes)}
                          onChange={e => editar(setor.id, mes, e.target.value)}
                          inputMode="decimal"
                          placeholder="—"
                          aria-label={`Meta de ${setor.nome} em ${MESES_ABREV[i]}`}
                          className={`h-8 w-16 px-1.5 text-center tabular-nums ${k in rascunho ? "border-info ring-1 ring-info/30" : ""}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-2">
                    <Button
                      size="sm" variant="ghost" className="h-8 px-2"
                      title="Repetir o valor do mês atual até dezembro"
                      onClick={() => repetirNoAno(setor.id, MES_ATUAL)}
                    >
                      <CopyPlus className="h-4 w-4" />
                      <span className="sr-only">Repetir para o resto do ano</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="border-t px-4 py-3 text-xs text-muted-foreground">
        Célula vazia = <b>sem meta</b>: o setor aparece com o valor cru e sem aderência — nunca como 0%.
        Editar grava nova vigência internamente (o histórico de metas não se reescreve, porque é ele que
        sustenta o indicador de aderência), mas aqui é só uma tabela. O botão <CopyPlus className="inline h-3 w-3" />{" "}
        repete o valor do mês atual até dezembro.
      </p>
    </div>
  );
}
